// A blueprint written in fields (byj-1wv.90): the keys an admin sets most,
// each a field with what it means under it (the key list's words), the
// shares of :coverage and :difficulty with their sum, :require's families as
// boxes, and the keys the fields do not hold named, kept as written and
// edited as EDN text. What is wrong is said under the fields, and the
// dialog's save waits for none.
import type { ReactNode } from "react";
import { type BlueprintFields, type BlueprintForm, type Problem, addsToOne, bands, families, shareSum } from "../blueprintForm";
import { competencies } from "../competencies";
import { t, word } from "../i18n";

/** A field of the form: its label, its input, and under it what the label does not say. */
function Field({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return (
    <label className="field">
      {label}
      {children}
      {hint && <span className="muted small">{hint}</span>}
    </label>
  );
}

/** The sum of some shares: coverage's said as right or not (they add to 1), difficulty's as what each share is a part of. */
function Sum({ values, testid, parts }: { values: string[]; testid: string; parts?: boolean }) {
  const sum = shareSum(values);
  return (
    <p className={`small flush ${parts || addsToOne(sum) ? "muted" : "warn"}`} data-testid={testid}>
      {t(parts ? "bank:bpShareParts" : "bank:bpShareSum", { sum: sum.toFixed(2) })}
    </p>
  );
}

export function BankBlueprintFields({ form, onChange, issues }: { form: BlueprintForm; onChange: (f: BlueprintForm) => void; issues: Problem[] }) {
  const f = form.fields;
  const set = (patch: Partial<BlueprintFields>) => onChange({ ...form, fields: { ...f, ...patch } });
  const text = (key: keyof BlueprintFields, label: string, hint?: string, inputMode?: "numeric" | "decimal") => (
    <Field label={label} hint={hint}>
      <input value={f[key] as string} inputMode={inputMode} onChange={(e) => set({ [key]: e.target.value } as Partial<BlueprintFields>)} aria-label={label} />
    </Field>
  );
  return (
    <div className="stack bank-bp-fields" data-testid="bank-blueprint-fields">
      <fieldset className="plain-fieldset stack tight">
        <legend className="small">{t("bank:bpFieldsPaper")}</legend>
        {text("title", t("bank:bpField.title"), t("bank:bpKeys.title"))}
        <div className="field-pair">
          {text("id", t("bank:bpField.id"), t("bank:bpKeys.id"))}
          {text("audience", t("bank:bpField.audience"), t("bank:bpFieldHint.audience"))}
        </div>
        <div className="field-pair">
          {text("minutes", t("bank:bpField.minutes"), undefined, "numeric")}
          {text("items", t("bank:bpField.items"), undefined, "numeric")}
        </div>
        <div className="field-pair">
          {text("passMark", t("bank:bpField.passMark"), undefined, "decimal")}
          {text("forms", t("bank:bpField.forms"), undefined, "numeric")}
        </div>
        <label className="toggle small">
          <input type="checkbox" checked={f.approval} onChange={(e) => set({ approval: e.target.checked })} />
          {t("bank:bpField.approval")}
        </label>
        <span className="muted small">{t("bank:bpFieldHint.approval")}</span>
      </fieldset>

      <fieldset className="plain-fieldset stack tight">
        <legend className="small">{t("bank:bpFieldsCoverage")}</legend>
        <p className="muted small flush">{t("bank:bpKeys.coverage")}</p>
        <div className="row wrap edit-numbers">
          {competencies.map((c) => (
            <label key={c} className="field">
              {word("bank:competency", c) ?? c}
              <input
                value={f.coverage[c]}
                inputMode="decimal"
                onChange={(e) => set({ coverage: { ...f.coverage, [c]: e.target.value } })}
                aria-label={word("bank:competency", c) ?? c}
              />
            </label>
          ))}
        </div>
        <Sum values={competencies.map((c) => f.coverage[c])} testid="bank-bp-coverage-sum" />
      </fieldset>

      <fieldset className="plain-fieldset stack tight">
        <legend className="small">{t("bank:bpFieldsDifficulty")}</legend>
        <p className="muted small flush">{t("bank:bpFieldHint.difficulty")}</p>
        <div className="row wrap edit-numbers">
          {bands.map((b) => (
            <label key={b} className="field">
              {word("bank:band", b) ?? b}
              <input
                value={f.difficulty[b]}
                inputMode="decimal"
                onChange={(e) => set({ difficulty: { ...f.difficulty, [b]: e.target.value } })}
                aria-label={word("bank:band", b) ?? b}
              />
            </label>
          ))}
        </div>
        {bands.some((b) => f.difficulty[b].trim() !== "") && <Sum values={bands.map((b) => f.difficulty[b])} testid="bank-bp-difficulty-sum" parts />}
      </fieldset>

      <fieldset className="plain-fieldset stack tight">
        <legend className="small">{t("bank:bpFieldsRequire")}</legend>
        <div className="row wrap">
          {families.map((fam) => (
            <label key={fam} className="toggle small">
              <input
                type="checkbox"
                checked={f.require.includes(fam)}
                onChange={(e) => set({ require: families.filter((x) => (x === fam ? e.target.checked : f.require.includes(x))) })}
              />
              {word("bank:family", fam) ?? fam}
            </label>
          ))}
        </div>
      </fieldset>

      <fieldset className="plain-fieldset stack tight">
        <legend className="small">{t("bank:bpFieldsKnowledge")}</legend>
        <p className="muted small flush">{t("bank:bpFieldHint.knowledge")}</p>
        <div className="row wrap edit-numbers">
          {(["items", "weight", "minutes"] as const).map((k) => (
            <label key={k} className="field">
              {t(`bank:bpField.knowledge.${k}`)}
              <input
                value={f.knowledge[k]}
                inputMode={k === "weight" ? "decimal" : "numeric"}
                onChange={(e) => set({ knowledge: { ...f.knowledge, [k]: e.target.value } })}
                aria-label={t(`bank:bpField.knowledge.${k}`)}
              />
            </label>
          ))}
        </div>
      </fieldset>

      {form.rest.length > 0 && (
        <p className="muted small flush" data-testid="bank-blueprint-rest">
          {t("bank:bpRest", { keys: form.rest.map((r) => r.key).join(", ") })}
        </p>
      )}
      {issues.length > 0 && (
        <ul className="small flush error bank-bp-problems" data-testid="bank-blueprint-problems">
          {issues.map((p) => (
            <li key={p}>{t(`bank:bpProblem.${p}`)}</li>
          ))}
        </ul>
      )}
    </div>
  );
}
